import { normalizeFr } from './normalize-fr';
import { FR_ABBREVIATIONS, FR_STOPWORDS } from './wordlists-fr';

/**
 * Tokenisation française légère : normalisation, mots, phrases, syllabes,
 * racines. Heuristiques assumées (pas de dictionnaire) : l'objectif est la
 * COHÉRENCE des mesures d'un article à l'autre, pas l'exactitude linguistique.
 */

export { normalizeFr };

const WORD_RE = /[a-z0-9]+/g;

/** Mots d'un texte NORMALISÉ (apostrophes et tirets = séparateurs). */
export function tokenizeWords(normalizedText: string): string[] {
  const out: string[] = [];
  const re = new RegExp(WORD_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(normalizedText)) !== null) out.push(m[0]);
  return out;
}

/** Mots « pleins » : sans mots vides ni nombres purs. */
export function contentWords(tokens: string[]): string[] {
  return tokens.filter((t): boolean => !FR_STOPWORDS.has(t) && !/^\d+$/.test(t) && t.length > 1);
}

const ABBR = new Set(FR_ABBREVIATIONS);

function isUpperLike(ch: string): boolean {
  return /[A-ZÀ-ÝŒ0-9«"“(]/.test(ch);
}

/**
 * Découpe en phrases sur le texte brut (accents conservés). Une fin de phrase =
 * [.!?…] suivi d'un espace et d'une majuscule / guillemet / chiffre, sauf après
 * une abréviation, une initiale ou un nombre décimal.
 */
export function splitSentences(text: string): string[] {
  const sentences: string[] = [];
  let start = 0;
  const n = text.length;
  for (let i = 0; i < n; i++) {
    const ch = text[i];
    if (ch !== '.' && ch !== '!' && ch !== '?' && ch !== '…') continue;
    // Groupe de ponctuation (« ?! », « ... »)
    let j = i;
    while (j + 1 < n && /[.!?…]/.test(text[j + 1])) j++;
    // Guillemet fermant, collé ou après une espace (typographie française : « non. »)
    const closing = text.slice(j + 1).match(/^[\s\u00a0]*[»"”)]/);
    if (closing) j += closing[0].length;
    const next = text.slice(j + 1).match(/^\s+(\S)/);
    if (!next) {
      i = j;
      continue;
    }
    if (!isUpperLike(next[1])) {
      i = j;
      continue;
    }
    if (ch === '.') {
      const before = text.slice(start, i);
      const lastWord = before.match(/([A-Za-zÀ-ÿ0-9]+)$/)?.[1] ?? '';
      const lw = lastWord.toLowerCase();
      if (ABBR.has(lw) || (lastWord.length === 1 && /[A-Z]/.test(lastWord))) {
        i = j;
        continue;
      }
      // « 1.000 » ou « 2.5 » : chiffre de part et d'autre du point
      if (/\d$/.test(before) && /^\d/.test(text.slice(i + 1))) continue;
    }
    sentences.push(text.slice(start, j + 1).trim());
    start = j + 1;
    i = j;
  }
  const tail = text.slice(start).trim();
  if (tail.length > 0) sentences.push(tail);
  return sentences.filter((s): boolean => s.length > 0);
}

const VOWELS = /[aeiouyàâäéèêëîïôöùûüÿ]+/g;

/** Syllabes approximatives d'un mot français (minuscule, accents conservés). */
export function countSyllablesFr(word: string): number {
  const w = word.toLowerCase().replace(/[^a-zà-ÿ]/g, '');
  if (w.length === 0) return 0;
  // « y » entre deux voyelles vaut consonne (ba-la-ya-ge) : on le neutralise avant de compter.
  const groups = w.replace(/([aeiouàâäéèêëîïôöùûü])y([aeiouàâäéèêëîïôöùûü])/g, '$1x$2').match(VOWELS);
  let count = groups ? groups.length : 1;
  if (count > 1) {
    // e muet final : « coupe », « coupes », « parlent »
    if (/[^é]e$/.test(w) || /[^é]es$/.test(w) || /ent$/.test(w)) count--;
  }
  // « ion », « ien » : diphtongues comptées 1 par le groupe, pas de correction.
  return Math.max(1, count);
}

/**
 * Racine approximative (stem) d'un mot NORMALISÉ. Règles minimales : pluriels,
 * féminins usuels, -aux. L'important est que deux formes du même mot donnent
 * la même racine, pas que la racine soit un mot.
 */
export function stemFr(token: string): string {
  let t = token;
  if (t.length <= 3) return t;
  const rules: [RegExp, string][] = [
    [/euses$/, 'eur'],
    [/euse$/, 'eur'],
    [/trices$/, 'teur'],
    [/trice$/, 'teur'],
    [/iennes$/, 'ien'],
    [/ienne$/, 'ien'],
    [/eaux$/, 'eau'],
    [/aux$/, 'al'],
    [/eres$/, 'er'],
    [/ere$/, 'er'],
    [/ements$/, 'ement'],
  ];
  for (const [re, rep] of rules) {
    if (re.test(t)) {
      t = t.replace(re, rep);
      return t;
    }
  }
  if (t.length > 3 && /[sx]$/.test(t) && !/ss$/.test(t)) t = t.slice(0, -1);
  if (t.length > 4 && /e$/.test(t)) t = t.slice(0, -1);
  return t;
}

export function stemAll(tokens: string[]): string[] {
  return tokens.map((t): string => stemFr(t));
}

/** Indice de lisibilité de Flesch adapté au français (Kandel & Moles), borné 0-100. */
export function fleschFr(words: number, sentences: number, syllables: number): number {
  if (words === 0 || sentences === 0) return 0;
  const asl = words / sentences;
  const asw = syllables / words;
  const score = 207 - 1.015 * asl - 73.6 * asw;
  return Math.max(0, Math.min(100, Math.round(score)));
}

/** Mots d'une phrase brute, normalisés. */
export function sentenceTokens(sentence: string): string[] {
  return tokenizeWords(normalizeFr(sentence));
}
